"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeftRight } from "lucide-react";
import Image from "next/image";
import { useCallback, useId, useMemo } from "react";
import { ChainIcon } from "@defuse/ui/chain-icon";
import { Button } from "@defuse/ui/button";
import { Combobox, type ComboboxItem } from "@defuse/ui/combobox";
import { Label } from "@defuse/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@defuse/ui/tooltip";
import { useBreakpoint } from "@/hooks/useScreenSize";
import { useTokens } from "@defuse/ui/tokens-provider";
import { type FilterTokens, type Tokens, getFilterTokens, getTokenIcon, getTokenLabel, tokenLabel } from "@intents-explorer/shared-api/tokens";
function labelsForState(tokenIds: string[] | undefined, chainIds: string[] | undefined, filterTokens: FilterTokens, tokens: Tokens): string[] | undefined {
  const idsByLabel = new Map<string, string[]>();
  const orphanIds: string[] = [];
  for (const id of tokenIds ?? []) {
    const label = getTokenLabel(tokens, id);
    if (label === undefined) {
      orphanIds.push(id);
      continue;
    }
    const arr = idsByLabel.get(label) ?? [];
    arr.push(id);
    idsByLabel.set(label, arr);
  }
  const tokenDisplay: string[] = [];
  for (const [label, ids] of idsByLabel) {
    const fullGroup = filterTokens.tokenIdsByLabel.get(label);
    const isFullGroup = fullGroup !== undefined && fullGroup.length === ids.length && fullGroup.every(id => {
      return ids.includes(id);
    });
    if (isFullGroup) {
      tokenDisplay.push(label);
    } else {
      tokenDisplay.push(...ids);
    }
  }
  const merged = [...new Set([...(chainIds ?? []), ...tokenDisplay, ...orphanIds])];
  return merged.length === 0 ? undefined : merged;
}
interface ChainAndTokensComboboxProps {
  id: string;
  label: string;
  filterTokens: FilterTokens;
  selectedChainOrToken: string[] | undefined;
  onSelect: (chainId: string) => void;
}
function ChainAndTokensCombobox(t0) {
  const $ = _c(20);
  const {
    id,
    label,
    filterTokens,
    selectedChainOrToken,
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      value: "",
      label: "All Chains and Tokens"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const allChainsAndTokens = t1;
  let t2;
  if ($[1] !== filterTokens.chains || $[2] !== filterTokens.tokenDisplayByChain || $[3] !== filterTokens.tokenIdsByLabel || $[4] !== selectedChainOrToken) {
    let t3;
    if ($[6] !== filterTokens.tokenDisplayByChain || $[7] !== filterTokens.tokenIdsByLabel) {
      t3 = chain => [{
        value: chain.id,
        label: `${chain.name} (chain)`,
        icon: <ChainIcon chain={chain.id} />,
        isGroupHeader: true,
        keywords: [chain.id]
      }, ...(filterTokens.tokenDisplayByChain.get(chain.id) ?? []).map(t4 => {
        const {
          id: id_0,
          symbol,
          chain: chain_0
        } = t4;
        return {
          value: tokenLabel({
            symbol,
            chain: chain_0
          }),
          label: tokenLabel({
            symbol,
            chain: chain_0
          }),
          icon: <TokenIcon token={{
            id: id_0,
            symbol,
            chain: chain_0
          }} />,
          keywords: [chain_0, ...(filterTokens.tokenIdsByLabel.get(tokenLabel({
            symbol,
            chain: chain_0
          })) ?? [id_0])]
        };
      })];
      $[6] = filterTokens.tokenDisplayByChain;
      $[7] = filterTokens.tokenIdsByLabel;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    const knownItems = filterTokens.chains.flatMap(t3);
    const knownValues = new Set(knownItems.map(_temp));
    const customItems = (selectedChainOrToken ?? []).filter(v => v !== "" && !knownValues.has(v)).map(_temp2);
    t2 = [allChainsAndTokens, ...customItems, ...knownItems];
    $[1] = filterTokens.chains;
    $[2] = filterTokens.tokenDisplayByChain;
    $[3] = filterTokens.tokenIdsByLabel;
    $[4] = selectedChainOrToken;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const chainItems = t2;
  let t3;
  if ($[9] !== id || $[10] !== label) {
    t3 = <Label htmlFor={id} className="text-muted-foreground text-xs">{label}</Label>;
    $[9] = id;
    $[10] = label;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== chainItems || $[13] !== id || $[14] !== onSelect || $[15] !== selectedChainOrToken) {
    t4 = <Combobox id={id} items={chainItems} value={selectedChainOrToken} onValueChange={onSelect} placeholder="Select a chain or a token..." searchPlaceholder="Search or paste a token id..." className="w-full md:w-[240px]" allowCustom={true} />;
    $[12] = chainItems;
    $[13] = id;
    $[14] = onSelect;
    $[15] = selectedChainOrToken;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <div className="flex w-full flex-col gap-1 md:w-auto">{t3}{t4}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function _temp2(v_0) {
  return {
    value: v_0,
    label: v_0
  };
}
function _temp(i) {
  return i.value;
}
function TokenIcon(t0) {
  const $ = _c(16);
  const {
    token
  } = t0;
  const t1 = useTokens();
  let t2;
  if ($[0] !== t1 || $[1] !== token.id) {
    t2 = getTokenIcon(t1, token.id);
    $[0] = t1;
    $[1] = token.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const tokenIcon = t2;
  let t3;
  if ($[3] !== token.symbol || $[4] !== tokenIcon) {
    t3 = <TooltipTrigger asChild={true}><Image width={16} height={16} className="h-4 min-h-4 w-4 min-w-4" src={tokenIcon} alt={token.symbol} /></TooltipTrigger>;
    $[3] = token.symbol;
    $[4] = tokenIcon;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== token.id) {
    t4 = <TooltipContent>{token.id}</TooltipContent>;
    $[6] = token.id;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <Tooltip>{t3}{t4}</Tooltip>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== token.chain) {
    t6 = <ChainIcon className="bg-background absolute -right-1 -bottom-1 h-3 w-3 rounded-sm p-0.5 shadow-sm" chain={token.chain} />;
    $[11] = token.chain;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <div className="relative h-4 min-h-4 w-4 min-w-4">{t5}{t6}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}
interface ChainFilters {
  fromChainId?: string[] | undefined;
  toChainId?: string[] | undefined;
  fromTokenId?: string[] | undefined;
  toTokenId?: string[] | undefined;
}
interface ChainsAndTokensFilterProps extends ChainFilters {
  tokenIdsFromOneClickDB: readonly string[];
  updateFilters: (filters: ChainFilters) => void;
}
function tokenFilterValue(value: string, filterTokens: FilterTokens): string[] {
  const ids = filterTokens.tokenIdsByLabel.get(value);
  return ids === undefined ? [value] : [...ids];
}
export function ChainsAndTokensFilter(t0) {
  const $ = _c(43);
  const {
    fromChainId,
    toChainId,
    fromTokenId,
    toTokenId,
    tokenIdsFromOneClickDB,
    updateFilters
  } = t0;
  const fromId = useId();
  const toId = useId();
  const tokens = useTokens();
  let t1;
  if ($[0] !== tokenIdsFromOneClickDB || $[1] !== tokens) {
    t1 = getFilterTokens(tokens, tokenIdsFromOneClickDB);
    $[0] = tokenIdsFromOneClickDB;
    $[1] = tokens;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const filterTokens = t1;
  let t2;
  if ($[3] !== filterTokens || $[4] !== fromChainId || $[5] !== fromTokenId || $[6] !== tokens) {
    t2 = labelsForState(fromTokenId, fromChainId, filterTokens, tokens);
    $[3] = filterTokens;
    $[4] = fromChainId;
    $[5] = fromTokenId;
    $[6] = tokens;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const currentFrom = t2;
  let t3;
  if ($[8] !== filterTokens || $[9] !== toChainId || $[10] !== toTokenId || $[11] !== tokens) {
    t3 = labelsForState(toTokenId, toChainId, filterTokens, tokens);
    $[8] = filterTokens;
    $[9] = toChainId;
    $[10] = toTokenId;
    $[11] = tokens;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const currentTo = t3;
  let t4;
  if ($[13] !== filterTokens || $[14] !== updateFilters) {
    t4 = value => {
      updateFilters(value === "" ? {
        fromChainId: undefined,
        fromTokenId: undefined
      } : isTokenId(value, filterTokens) ? {
        fromTokenId: tokenFilterValue(value, filterTokens),
        fromChainId: undefined
      } : {
        fromChainId: [value],
        fromTokenId: undefined
      });
    };
    $[13] = filterTokens;
    $[14] = updateFilters;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleFromChange = t4;
  let t5;
  if ($[16] !== filterTokens || $[17] !== updateFilters) {
    t5 = value_0 => {
      updateFilters(value_0 === "" ? {
        toChainId: undefined,
        toTokenId: undefined
      } : isTokenId(value_0, filterTokens) ? {
        toTokenId: tokenFilterValue(value_0, filterTokens),
        toChainId: undefined
      } : {
        toChainId: [value_0],
        toTokenId: undefined
      });
    };
    $[16] = filterTokens;
    $[17] = updateFilters;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const handleToChange = t5;
  let t6;
  if ($[19] !== fromChainId || $[20] !== fromTokenId || $[21] !== toChainId || $[22] !== toTokenId || $[23] !== updateFilters) {
    t6 = () => {
      updateFilters({
        fromTokenId: toTokenId,
        fromChainId: toTokenId !== undefined ? undefined : toChainId,
        toTokenId: fromTokenId,
        toChainId: fromTokenId !== undefined ? undefined : fromChainId
      });
    };
    $[19] = fromChainId;
    $[20] = fromTokenId;
    $[21] = toChainId;
    $[22] = toTokenId;
    $[23] = updateFilters;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const handleSwitch = t6;
  const isDesktop = useBreakpoint("md");
  let t7;
  if ($[25] !== currentFrom || $[26] !== filterTokens || $[27] !== fromId || $[28] !== handleFromChange) {
    t7 = <ChainAndTokensCombobox id={fromId} label="From" filterTokens={filterTokens} selectedChainOrToken={currentFrom} onSelect={handleFromChange} />;
    $[25] = currentFrom;
    $[26] = filterTokens;
    $[27] = fromId;
    $[28] = handleFromChange;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const t8 = isDesktop ? "ghost" : "outline";
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowLeftRight className="h-4 w-4" />;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== handleSwitch || $[32] !== t8) {
    t10 = <Button variant={t8} size="icon" className="mt-3 md:mt-5" onClick={handleSwitch} title="Switch chains">{t9}</Button>;
    $[31] = handleSwitch;
    $[32] = t8;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== currentTo || $[35] !== filterTokens || $[36] !== handleToChange || $[37] !== toId) {
    t11 = <ChainAndTokensCombobox id={toId} label="To" filterTokens={filterTokens} selectedChainOrToken={currentTo} onSelect={handleToChange} />;
    $[34] = currentTo;
    $[35] = filterTokens;
    $[36] = handleToChange;
    $[37] = toId;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== t10 || $[40] !== t11 || $[41] !== t7) {
    t12 = <div className="flex flex-col items-center gap-2 md:grid md:grid-cols-[1fr_auto_1fr]">{t7}{t10}{t11}</div>;
    $[39] = t10;
    $[40] = t11;
    $[41] = t7;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}
function isTokenId(value: string, filterTokens: FilterTokens): boolean {
  return !filterTokens.chains.some(chain => {
    return chain.id === value;
  });
}